<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    type StepperStep,
    type StepPosition,
    type StepsStyle,
    type StepStyle,
    type DividerStyle,
} from './types';

type ColorSizeProp = {
    mobile: string;
    desktop: string;
};

type StyleProp = {
    backgroundColor: ColorSizeProp;
    borderColor: ColorSizeProp;
    iconColor: ColorSizeProp;
    titleColor: ColorSizeProp;
};

type StepperStepProp = {
    title?: {
        desktop: string;
        mobile: string;
    };
    icon: {
        desktop: IconDefinition;
        mobile: IconDefinition;
    };
    clickable?: boolean;
    disabled?: boolean;
    position: StepPosition;
    styles: Record<StepPosition, StyleProp>;
};
interface IProps {
    steps?: StepperStep[];
    navigation?: 'free' | 'strict' | 'history';
    stepStyles?: StepsStyle;
    dividerStyles?: DividerStyle;
}
const {
    steps = [],
    navigation = 'history',
    stepStyles,
    dividerStyles = {
        activeColor: 'var(--blue-300)',
        inactiveColor: 'white',
    },
} = defineProps<IProps>();

const modelValue = defineModel<number>({ default: 0 });

const stepPosition = (index: number) => {
    switch (true) {
        case index > modelValue.value:
            return 'next';
        case index < modelValue.value:
            return 'previous';
        case index === modelValue.value:
        default:
            return 'current';
    }
};
const { isMobile } = useSize(true);

const sizeKey = computed(() => (isMobile.value ? 'mobile' : 'desktop'));

const shownSteps = computed(() => steps.filter(step => !step.hidden));

const clickDisabled = (index: number) => {
    const step = shownSteps.value[index];
    if (step.disabled || step.readonly) {
        return true;
    }
    switch (navigation) {
        case 'free':
            return false;
        case 'strict':
            return true;
        case 'history':
        default:
            return modelValue.value < index;
    }
};

const extractStyles = (index: number) => {
    const step = shownSteps.value[index];
    // default values
    const result = {
        current: {
            backgroundColor: {
                mobile: 'transparent',
                desktop: 'transparent',
            },
            borderColor: {
                mobile: 'white',
                desktop: 'white',
            },
            iconColor: {
                desktop: 'white',
                mobile: 'var(--blue-400)',
            },
            titleColor: {
                mobile: 'var(--blue-400)',
                desktop: 'white',
            },
        },
        next: {
            backgroundColor: {
                mobile: 'var(--blue-300)',
                desktop: 'var(--blue-300)',
            },
            borderColor: {
                mobile: 'transparent',
                desktop: 'transparent',
            },
            iconColor: {
                desktop: 'var(--blue-100)',
                mobile: 'var(--cool-gray-700)',
            },
            titleColor: {
                mobile: 'var(--cool-gray-700)',
                desktop: 'white',
            },
        },
        previous: {
            backgroundColor: {
                mobile: 'white',
                desktop: 'white',
            },
            borderColor: {
                mobile: 'transparent',
                desktop: 'transparent',
            },
            iconColor: {
                desktop: 'var(--blue-400)',
                mobile: 'var(--green-400)',
            },
            titleColor: {
                mobile: 'var(--green-400)',
                desktop: 'white',
            },
        },
    };
    // global values
    if (stepStyles) {
        for (const stepStyle in stepStyles) {
            const position = stepStyle as keyof typeof stepStyles;
            const styleCollection = stepStyles[position];
            for (const style in styleCollection) {
                const key = style as keyof typeof styleCollection;
                const s = styleCollection[key];
                const sizes = useExtractSize(s);
                if (sizes.mobile.value) {
                    result[position][key].mobile = sizes.mobile.value;
                }
                if (sizes.desktop.value) {
                    result[position][key].desktop = sizes.desktop.value;
                }
            }
        }
    }
    // specific override
    if (step.styleOverride) {
        const override = step.styleOverride;
        if ('current' in override) {
            // per position
            const stylesCollection = override as StepsStyle;
            for (const stepStyle in stylesCollection) {
                const position = stepStyle as keyof typeof stylesCollection;
                const styleCollection = stylesCollection[position];
                for (const style in styleCollection) {
                    const key = style as keyof typeof styleCollection;
                    const s = styleCollection[key];
                    const sizes = useExtractSize(s);
                    if (sizes.mobile.value) {
                        result[position][key].mobile = sizes.mobile.value;
                    }
                    if (sizes.desktop.value) {
                        result[position][key].desktop = sizes.desktop.value;
                    }
                }
            }
        } else {
            // one for all
            const styleCollection = override as StepStyle;
            for (const position of ['previous', 'current', 'next'] as const) {
                for (const style in styleCollection) {
                    const key = style as keyof typeof styleCollection;
                    const s = styleCollection[key];
                    const sizes = useExtractSize(s);
                    if (sizes.mobile.value) {
                        result[position][key].mobile = sizes.mobile.value;
                    }
                    if (sizes.desktop.value) {
                        result[position][key].desktop = sizes.desktop.value;
                    }
                }
            }
        }
    }
    return result;
};

const normalizedSteps = computed<StepperStepProp[]>(() =>
    shownSteps.value.map((step, index) => {
        const position = stepPosition(index);
        const styles = extractStyles(index);
        const icons = useExtractSize(step.icon);
        const titles = useExtractSize(step.title);
        return {
            title: {
                desktop: titles.desktop.value ?? '',
                mobile: titles.mobile.value ?? '',
            },
            clickable: !clickDisabled(index),
            disabled: step.disabled,
            icon: {
                desktop: icons.desktop.value!,
                mobile: icons.mobile.value!,
            },
            position,
            styles,
        } as const;
    })
);

const click = (index: number) => {
    if (!normalizedSteps.value[index].clickable) {
        return;
    }
    modelValue.value = index;
};
</script>
<template>
    <div class="mr-stepper-root">
        <template
            v-for="(step, index) in normalizedSteps"
            :key="`content-${step.title || index}`"
        >
            <div
                class="step-content"
                :class="{
                    clickable: step.clickable,
                    disabled: step.disabled,
                }"
                @click.stop="click(index)"
            >
                <div
                    class="icon-wrapper"
                    :style="{
                        '--step-background-color':
                            step.styles[step.position].backgroundColor[sizeKey],
                        '--step-border-color':
                            step.styles[step.position].borderColor[sizeKey],
                    }"
                >
                    <mr-icon
                        :icon="step.icon[sizeKey]"
                        :color="step.styles[step.position].iconColor[sizeKey]"
                        size="small"
                    />
                </div>
                <mr-typography
                    v-if="step.title"
                    white-space="nowrap"
                    :color="step.styles[step.position].titleColor[sizeKey]"
                >
                    {{ step.title[sizeKey] }}
                </mr-typography>
            </div>
            <mr-divider
                v-if="index !== normalizedSteps.length - 1"
                :key="`divider-${step.title}`"
                :color="
                    modelValue <= index
                        ? dividerStyles.inactiveColor
                        : dividerStyles.activeColor
                "
                border-style="dashed"
                class="step-divider"
            />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.mr-stepper-root {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    gap: 16px;

    .step-content {
        width: 40px;
        height: 40px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: center;
        flex-shrink: 0;
        &.clickable {
            cursor: pointer;
        }
        &.disabled {
            cursor: not-allowed;
        }
        .icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            flex-shrink: 0;
            width: 40px;
            height: 40px;
            background-color: var(--step-background-color);
            border: 1px solid var(--step-border-color);
            box-sizing: border-box;
        }
    }

    .step-divider {
        align-self: stretch;
    }
}
</style>
